import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileDown, Sparkles, UserPlus, SearchCheck, PlugZap } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StageAge } from "@/components/crm/bits";
import { DuplicateBox } from "@/components/crm/AddLeadDialog";

const Panel = ({ icon: Icon, title, text, children, testid }) => (
  <section className="card-surface p-5" data-testid={testid}>
    <div className="flex items-start gap-3 mb-4">
      <span className="h-8 w-8 rounded-lg bg-[#ECF5F1] grid place-items-center shrink-0"><Icon className="h-4 w-4 text-[#0B3C2D]" /></span>
      <div><h2 className="font-display text-[15px] font-semibold">{title}</h2><p className="text-xs text-gray-500 mt-0.5">{text}</p></div>
    </div>
    {children}
  </section>
);

function ImportPanel() {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const { bump } = useMeta();
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/leads/import", fd); setRes(r.data); toast.success(`${r.data.created} lead(s) imported`); bump(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <Panel icon={Upload} title="Import from Excel / CSV" text="Duplicates (company, email or phone) are detected and skipped automatically." testid="import-panel">
      <div className="flex flex-wrap gap-2">
        <input ref={ref} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" disabled={busy} onClick={() => ref.current.click()} data-testid="import-upload-button"><Upload className="h-4 w-4 mr-1.5" />{busy ? "Importing…" : "Choose file"}</Button>
        <Button variant="outline" onClick={() => downloadFile("/leads/import/template", "thakker-one-import-template.csv")} data-testid="import-template-button"><FileDown className="h-4 w-4 mr-1.5" />Template</Button>
      </div>
      {res && (
        <div className="mt-4 text-sm space-y-2" data-testid="import-result">
          <p><strong className="text-[#0B3C2D]">{res.created}</strong> created · <strong>{res.skipped.length}</strong> skipped · <strong>{res.errors.length}</strong> errors</p>
          {[...res.skipped, ...res.errors].slice(0, 8).map((s) => <p key={`${s.row}-${s.company}`} className="text-xs text-gray-500">Row {s.row}: {s.company} — {s.reason}</p>)}
        </div>
      )}
    </Panel>
  );
}

function DuplicatePanel() {
  const [f, setF] = useState({ company: "", email: "", phone: "" });
  const [dups, setDups] = useState(null);
  const nav = useNavigate();
  const check = async () => { const r = await api.get("/leads/check-duplicates", { params: f }); setDups(r.data.duplicates); };
  return (
    <Panel icon={SearchCheck} title="Duplicate check" text="Check before calling a new prospect." testid="duplicate-panel">
      <div className="grid sm:grid-cols-3 gap-2">
        {["company", "email", "phone"].map((k) => <Input key={k} data-testid={`dup-${k}-input`} placeholder={k[0].toUpperCase() + k.slice(1)} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9" />)}
      </div>
      <Button variant="outline" className="mt-3" onClick={check} data-testid="dup-check-button">Check</Button>
      {dups && (dups.length ? <div className="mt-3"><DuplicateBox dups={dups} onOpen={(d) => nav(`/leads/${d.lead_id}`)} /></div>
        : <p className="text-sm text-[#0B3C2D] mt-3" data-testid="dup-none">No existing lead matches — good to add.</p>)}
    </Panel>
  );
}

function QualifyQueue() {
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();
  const { version } = useMeta();
  const load = () => api.get("/leads", { params: { limit: 100, sort: "created_at" } }).then((r) => setLeads(r.data.items.filter((l) => l.ai_score == null && l.age_status !== "closed").slice(0, 6)));
  useEffect(() => { load(); }, [version]);
  const qualify = async (l) => {
    setBusy(l.lead_id);
    try { const r = await api.post(`/ai/leads/${l.lead_id}/qualify`); toast.success(`${l.company}: ${r.data.ai_score}/100`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <Panel icon={Sparkles} title="AI qualification queue" text="Unscored active leads. AI scores fit, volume and engagement, then suggests a next step." testid="ai-qualify-queue-panel">
      {leads.length === 0 ? <p className="text-sm text-gray-500">All active leads have been scored.</p> : (
        <div className="divide-y divide-gray-50">
          {leads.map((l) => (
            <div key={l.lead_id} className="flex items-center gap-3 py-2.5">
              <button className="min-w-0 flex-1 text-left" onClick={() => nav(`/leads/${l.lead_id}`)}><p className="text-sm font-medium truncate">{l.company}</p><p className="text-xs text-gray-500">{l.industry || "—"} · {l.owner_name}</p></button>
              <StageAge lead={l} />
              <Button size="sm" variant="outline" className="border-[#A3D9C9] text-[#0B3C2D]" disabled={!!busy} onClick={() => qualify(l)} data-testid={`qualify-${l.lead_id}`}>{busy === l.lead_id ? "Scoring…" : "Qualify"}</Button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function SourcesPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports").then((r) => setRows(r.data.by_source)); }, []);
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel icon={PlugZap} title="Lead sources" text="Where your leads come from. Direct connectors (IndiaMART, TradeIndia, website forms) can be added later — they need API access from those providers." testid="sources-panel">
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.name} className="text-sm"><div className="flex justify-between"><span className="text-gray-700">{r.name}</span><span className="font-mono-num text-gray-500">{r.count} · {r.won} won</span></div>
            <div className="h-1 bg-gray-100 rounded-full mt-1"><div className="h-full bg-[#0B3C2D] rounded-full" style={{ width: `${(r.count / max) * 100}%` }} /></div></div>
        ))}
      </div>
    </Panel>
  );
}

export default function LeadEngine() {
  const { setAddLeadOpen } = useMeta();
  return (
    <div className="fade-in" data-testid="lead-engine-page">
      <PageHeader title="Lead Engine" subtitle="Capture, de-duplicate and qualify new prospects.">
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={() => setAddLeadOpen(true)} data-testid="engine-add-lead-button"><UserPlus className="h-4 w-4 mr-1.5" />Add Lead</Button>
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4 min-w-0"><ImportPanel /><DuplicatePanel /></div>
        <div className="space-y-4 min-w-0"><QualifyQueue /><SourcesPanel /></div>
      </div>
    </div>
  );
}
